import { useEffect } from "react";
import type { APIEarnedAchievement, APIGameAnalysisOverview } from "@rosette/ts/src/business/api/api_schema.js";
import { AchievementType } from "@rosette/ts/src/business/achievement/AchievementType.js";
import { featureFlags } from "@rosette/ts/src/business/featureFlags.js";
import { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import { useToastActions } from "@rosette/web_components/feedback/toast/ToastContext.js";
import { useAchievementResources } from "@/components/achievements/useAchievementResources.js";
import { addEarnedAchievementToasts } from "@/utils/achievements/addEarnedAchievementToasts.js";


/**
 * Shows toasts for achievements that were granted during a game's analysis
 * (e.g. accuracy and rating milestones), once the analysis becomes available
 * on the win screen.
 */
export function useAnalysisAchievementToasts(
    gameID: string | null,
    analysisOverview: APIGameAnalysisOverview | null | undefined,
    yourPlayer: PlayerType | null,
): void {
    const { addToast } = useToastActions();
    const achievementResources = useAchievementResources();

    useEffect(() => {
        if (!featureFlags.achievementsEnabled)
            return;
        if (!gameID || !analysisOverview || !yourPlayer)
            return;

        const newAchievements = analysisOverview.newAchievements;
        const achievementIDs = (yourPlayer === PlayerType.LIGHT
            ? newAchievements?.light : newAchievements?.dark);
        if (!achievementIDs || achievementIDs.length === 0)
            return;

        // Only toast once per game, even across page reloads.
        const storageKey = `analysis-achievements-toasted:${gameID}`;
        if (window.sessionStorage.getItem(storageKey) !== null)
            return;
        window.sessionStorage.setItem(storageKey, "true");

        const earnedAchievements: APIEarnedAchievement[] = achievementIDs
            .filter(id => AchievementType.getByIDOrNull(id) !== null)
            .map(id => ({
                already_earned: false,
                should_notify: true,
                achievement_id: id,
                earned_at: analysisOverview.createdAt.toISOString(),
            }));
        addEarnedAchievementToasts(earnedAchievements, addToast, achievementResources);
    }, [gameID, analysisOverview, yourPlayer, addToast, achievementResources]);
}
